/**
 * TerrainGenerator — 2D Wave Function Collapse map generator.
 *
 * Replaces the old cellular-automata approach with a proper WFC solver driven
 * by adjacency rules defined in public/assets/tilesets/tileset.json.
 *
 * Tile index convention (matches tileset.json and the sprite sheet):
 *   1 = grass   (walkable)
 *   2 = wall    (non-walkable, blocks movement and projectiles)
 *   3 = road    (walkable)
 *   4 = tree    (non-walkable)
 */

import { WFCSolver } from './WFCSolver';
import type { TilesetConfig } from './TilesetLoader';

export interface GeneratedMap {
  width: number;
  height: number;
  /** tiles[row][col] — tile index (1 = grass, 2 = wall, 3 = road, 4 = tree). */
  tiles: number[][];
  /** Reference to the tileset config used to generate this map (for walkability lookups). */
  tilesetConfig?: TilesetConfig;
}

/** Simple seeded PRNG (Mulberry32) — kept for post-processing steps. */
function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return (): number => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class TerrainGenerator {
  private tilesetConfig: TilesetConfig | null = null;

  /**
   * Supply the loaded tileset config before calling generate().
   * If not supplied the generator will use a built-in default config so it
   * continues to work even if PreloadScene hasn't injected the JSON yet.
   */
  setTilesetConfig(config: TilesetConfig): void {
    this.tilesetConfig = config;
  }

  generate(width: number, height: number, seed?: number, options?: { skipSpawnZones?: boolean }): GeneratedMap {
    const actualSeed = seed ?? Math.floor(Math.random() * 0xffffffff);
    const rng = makeRng(actualSeed);
    const skipSpawnZones = options?.skipSpawnZones ?? false;

    const config = this.tilesetConfig ?? this.buildDefaultConfig();

    // ── Build WFC constraints ──────────────────────────────────────────────
    // Border cells must be walls; spawn zones must be grass.
    const constraints: { row: number; col: number; tileId: string }[] = [];

    // Solid border of walls
    for (let c = 0; c < width; c++) {
      constraints.push({ row: 0,          col: c, tileId: 'wall' });
      constraints.push({ row: height - 1, col: c, tileId: 'wall' });
    }
    for (let r = 1; r < height - 1; r++) {
      constraints.push({ row: r, col: 0,         tileId: 'wall' });
      constraints.push({ row: r, col: width - 1, tileId: 'wall' });
    }

    if (!skipSpawnZones) {
      // Player spawn zone (rows 2–6, cols 2–6) → grass
      for (let r = 2; r <= Math.min(6, height - 2); r++) {
        for (let c = 2; c <= Math.min(6, width - 2); c++) {
          constraints.push({ row: r, col: c, tileId: 'grass' });
        }
      }

      // Enemy spawn zone (rows 2–14, cols 10–18) → grass
      for (let r = 2; r <= Math.min(14, height - 2); r++) {
        for (let c = Math.min(10, width - 2); c <= Math.min(width - 2, 18); c++) {
          constraints.push({ row: r, col: c, tileId: 'grass' });
        }
      }
    }

    // ── Run WFC ───────────────────────────────────────────────────────────
    const solver = new WFCSolver(config);
    const wfcResult = solver.solve(width, height, actualSeed, constraints);
    const tiles = wfcResult.tiles;

    // ── Post-processing ────────────────────────────────────────────────────

    if (!skipSpawnZones) {
      // Re-enforce spawn zones (WFC may have overwritten them due to propagation)
      this.clearSpawnZones(tiles, width, height);
    }

    // Add roads — one horizontal + one vertical through walkable interior cells
    this.addRoads(tiles, width, height, rng);

    // Flood-fill from player spawn to wall off isolated walkable pockets
    this.floodFillIsolated(tiles, width, height, 3, 3);

    if (!skipSpawnZones) {
      // Final spawn zone clear after flood-fill
      this.clearSpawnZones(tiles, width, height);
    }

    return { width, height, tiles, tilesetConfig: config };
  }

  // ── Post-processing helpers ───────────────────────────────────────────────

  private clearSpawnZones(tiles: number[][], width: number, height: number): void {
    // Player spawn area
    for (let r = 2; r <= Math.min(6, height - 2); r++) {
      for (let c = 2; c <= Math.min(6, width - 2); c++) {
        tiles[r][c] = 1; // grass
      }
    }
    // Enemy spawn zone
    for (let r = 2; r <= Math.min(14, height - 2); r++) {
      for (let c = Math.min(10, width - 2); c <= Math.min(width - 2, 18); c++) {
        tiles[r][c] = 1; // grass
      }
    }
  }

  private addRoads(
    tiles: number[][],
    width: number,
    height: number,
    rng: () => number
  ): void {
    // Horizontal road at a random row in the middle third
    const hRow = Math.floor(height * 0.35 + rng() * height * 0.3);
    for (let c = 1; c < width - 1; c++) {
      if (tiles[hRow][c] === 1) tiles[hRow][c] = 3; // road over grass only
    }
    // Vertical road at a random col in the middle third
    const vCol = Math.floor(width * 0.35 + rng() * width * 0.3);
    for (let r = 1; r < height - 1; r++) {
      if (tiles[r][vCol] === 1) tiles[r][vCol] = 3; // road over grass only
    }
  }

  private floodFillIsolated(
    tiles: number[][],
    width: number,
    height: number,
    startCol: number,
    startRow: number
  ): void {
    const visited: boolean[][] = Array.from({ length: height }, () =>
      new Array(width).fill(false)
    );
    const queue: [number, number][] = [[startRow, startCol]];
    visited[startRow][startCol] = true;

    while (queue.length > 0) {
      const [r, c] = queue.shift()!;
      for (const [nr, nc] of [[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]] as [number, number][]) {
        if (nr < 0 || nr >= height || nc < 0 || nc >= width) continue;
        if (visited[nr][nc]) continue;
        if (tiles[nr][nc] === 1 || tiles[nr][nc] === 3) {
          visited[nr][nc] = true;
          queue.push([nr, nc]);
        }
      }
    }

    // Isolate any unreachable walkable cell by converting it to wall
    for (let r = 1; r < height - 1; r++) {
      for (let c = 1; c < width - 1; c++) {
        if ((tiles[r][c] === 1 || tiles[r][c] === 3) && !visited[r][c]) {
          tiles[r][c] = 2; // wall
        }
      }
    }
  }

  // ── Default config (fallback when tileset.json isn't loaded) ─────────────

  private buildDefaultConfig(): TilesetConfig {
    return {
      tiles: [
        {
          id: 'grass',
          index: 1,
          walkable: true,
          collisionShape: 'none',
          weight: 6,
          edges: {
            N: ['grass', 'wall', 'road', 'tree'],
            E: ['grass', 'wall', 'road', 'tree'],
            S: ['grass', 'wall', 'road', 'tree'],
            W: ['grass', 'wall', 'road', 'tree'],
          },
        },
        {
          id: 'wall',
          index: 2,
          walkable: false,
          collisionShape: 'full',
          weight: 2,
          edges: {
            N: ['wall', 'grass', 'tree'],
            E: ['wall', 'grass', 'tree'],
            S: ['wall', 'grass', 'tree'],
            W: ['wall', 'grass', 'tree'],
          },
        },
        {
          id: 'road',
          index: 3,
          walkable: true,
          collisionShape: 'none',
          weight: 2,
          edges: {
            N: ['road', 'grass'],
            E: ['road', 'grass'],
            S: ['road', 'grass'],
            W: ['road', 'grass'],
          },
        },
        {
          id: 'tree',
          index: 4,
          walkable: false,
          collisionShape: 'full',
          weight: 1,
          edges: {
            N: ['tree', 'grass', 'wall'],
            E: ['tree', 'grass', 'wall'],
            S: ['tree', 'grass', 'wall'],
            W: ['tree', 'grass', 'wall'],
          },
        },
      ],
    };
  }
}
